import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MortgageOfferValidation } from 'shared/components/MortgageOfferValidation';
import { OfferList } from 'shared/components/OfferList';
import { ShortOffersEmpty } from 'shared/components/ShortOffersEmpty';
import { ShortOffersList } from 'shared/components/ShortOffersList';
import { selectCalculatorErrors, selectCalculatorValues } from 'shared/selectors/landing';
import { ERuntimeSettings } from 'shared/types/runtimeSettings';
import { ETelemetryEventType } from 'shared/types/telemetry';
import { Loader } from '../../components/Loader';
import { MortgageOfferEmpty } from '../../components/MortgageOfferEmpty';
import { selectBanksLogos } from '../../selectors/dictionaries';
import { TMapOffersResult } from '../../types/common/offer';
import { useApplicationContext } from '../../utils/applicationContext';
import { useTracking } from '../../utils/tracking/useTracking';
import { getObjectForAnalyticsAboutShowingOffers } from '../../utils/tracking/utils/getObjectForAnalyticsAboutShowingOffers';

export interface IListingContainerProps {
  mappedOffersList: TMapOffersResult;
  offersIsFetching: boolean;
}

export const ListingContainer: React.FC<IListingContainerProps> = ({ mappedOffersList, offersIsFetching }) => {
  const { trackShowOffers } = useTracking();
  const { telemetry, config } = useApplicationContext();
  const calculatorValues = useSelector(selectCalculatorValues);
  const banksLogos = useSelector(selectBanksLogos);
  const calculatorErrors = useSelector(selectCalculatorErrors);
  const mortgageLandingRedesignEnabled = config.get<boolean>(ERuntimeSettings.mortgageLandingRedesign);

  const canShowOffersListing = Boolean(calculatorValues) && calculatorErrors.length === 0;
  const showsEmptyOffersListing = canShowOffersListing && !offersIsFetching && mappedOffersList.length === 0;

  useEffect(() => {
    if (!offersIsFetching) {
      trackShowOffers(getObjectForAnalyticsAboutShowingOffers(mappedOffersList));
    }
  }, [offersIsFetching, mappedOffersList.length, trackShowOffers, mappedOffersList]);

  useEffect(() => {
    if (showsEmptyOffersListing) {
      telemetry.increment(ETelemetryEventType.EmptyOffersListingShown);
    }
  }, [showsEmptyOffersListing, telemetry]);

  if (!canShowOffersListing) {
    return <MortgageOfferValidation errors={calculatorErrors} bankList={banksLogos} />;
  }

  if (offersIsFetching) {
    return <Loader text={'Загружаем лучшие предложения...'} />;
  }

  if (showsEmptyOffersListing) {
    return mortgageLandingRedesignEnabled ? <ShortOffersEmpty /> : <MortgageOfferEmpty />;
  }

  return mortgageLandingRedesignEnabled ? (
    <ShortOffersList offersList={mappedOffersList} />
  ) : (
    <OfferList offersList={mappedOffersList} />
  );
};
